import { Converter } from 'showdown';
import axios from 'smg/axios';
import { GetPrice, GetSizes, LowestQuantity } from 'smg/customer-api/lineitems';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { Relref } from '../hugo';

declare interface searchResp {
    docs: {
        _formatted: {
            author: string;
            date: string;
            lastmod: string;
            section: string;
            subsections: string;
            summary: string;
            title: string;
            url: string;
        };
        author: string;
        date: number;
        lastmod: number;
        section: string;
        subsections: string;
        summary: string;
        html: string;
        title: string;
        url: string;
        type: string;
        images: string[];
        tags: {
            relPermalink: string;
            title: string;
        }[];
        params: {
            readingduration: string;
            minqty: number;
            product: string;
            use: string;
            producttype: string;
        };
        page: string;
    }[];
    pageFacetDistribution: Record<string, number>;
}

if (document.body.id === 'search') {
    void main();
}

async function main() {
    const q = G().Get('q') || '';
    const $searchInputs = document.querySelectorAll<HTMLInputElement>('.header-search-input');
    $searchInputs.forEach($el => $el.value = q);

    const $searchButtons = document.querySelectorAll<HTMLButtonElement>('.header-search-button');
    $searchButtons.forEach($el => $el.dispatchEvent(new Event('click')));

    const searchResults = await axios.get<searchResp>('/v2/search', {
        params: {
            'q': q,
            'filter': G().Get('filter'),
        },
    }).then(resp => resp.data);

    const results = searchResults.docs;
    const facetDist = new Map<string, number>(Object.entries(searchResults.pageFacetDistribution));

    const $spinner = document.querySelector('#search-spinner') as HTMLElement;
    const $results = document.querySelector('#search-results') as HTMLElement;
    const $resultsQtyHeader = $results.querySelector('.search-qty-header') as HTMLElement;

    const $count = document.querySelectorAll<HTMLElement>('.search-count');
    $count.forEach($el => $el.textContent = Intl.NumberFormat().format(results.length));

    const $term = document.querySelectorAll<HTMLElement>('.search-term');
    $term.forEach($el => $el.textContent = q);

    const noResultsMessage = document.getElementById('no-results-message') as HTMLElement;

    const $resultTemplate = document.querySelector('#search-result-template') as HTMLTemplateElement;
    const discoverResults = [];
    const usesResults = [];
    const pageResults = [];
    const faqResults = [];
    for (const result of results) {
        if (result.page === 'discover') {
            discoverResults.push(result);
            continue;
        }

        if (result.page === 'use') {
            usesResults.push(result);
            continue;
        }

        if (result.page === 'faq') {
            faqResults.push(result);
            continue;
        }

        pageResults.push(result);
        if (pageResults.length > 4) {
            break;
        }

        const $pageResultsContainer = document.querySelector('#page-results-container') as HTMLElement;
        $pageResultsContainer.classList.remove('d-none');
        const $pageResultsInnerContainer = $pageResultsContainer.querySelector('.page-results-inner-container') as HTMLElement;

        const $result = template.Clone($resultTemplate).firstElementChild as HTMLAnchorElement;
        $result.href = result.url;

        const $title = $result.querySelector('.search-result-title') as HTMLElement;
        $title.textContent = result.title;

        const $description = $result.querySelector('.search-result-description') as HTMLElement;
        $description.innerHTML = result._formatted.summary;

        $pageResultsInnerContainer.appendChild($result);
    }

    const $pageResultsHeader = document.querySelector('.page-results-header') as HTMLElement;
    if (pageResults.length === results.length) {
        $pageResultsHeader.classList.add('d-none');
    } else {
        const jsonCount = pageResults.filter(result => result.url.endsWith('.json')).length;
        $pageResultsHeader.classList.remove('d-none');
        $pageResultsHeader.textContent = `Other Pages ${facetDist.get('other') || facetDist.get('type') ? `(${((facetDist.get('other') || 0) + (facetDist.get('type') || 0)) - jsonCount})` : ''}`;
    }
    const $viewMorePageResultsBtn = document.querySelector('.view-more-page-results-btn') as HTMLAnchorElement;
    $viewMorePageResultsBtn.href = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}filter=${encodeURIComponent("page != 'discover' AND (page != 'use')")}`;

    if (discoverResults.length > 0) {
        $pageResultsHeader.classList.remove('d-none');
        $viewMorePageResultsBtn.parentElement?.classList.remove('d-none');
        createDiscoverResults(discoverResults, results.length, facetDist);
        if (discoverResults.length === results.length) {
            $resultsQtyHeader.classList.add('d-none');
        }
    } else {
        if (discoverResults.length === results.length) {
            $resultsQtyHeader.classList.add('d-none');
            noResultsMessage.classList.remove('d-none');
        }
    }

    if (usesResults.length > 0) {
        $pageResultsHeader.classList.remove('d-none');
        $viewMorePageResultsBtn.parentElement?.classList.remove('d-none');
        await createUsesResults(usesResults, results.length, facetDist);
        if (usesResults.length === results.length) {
            $resultsQtyHeader.classList.add('d-none');
        }
    }

    if (faqResults.length > 0) {
        createFaqResults(faqResults, results.length, facetDist);
        if (faqResults.length === results.length) {
            $resultsQtyHeader.classList.add('d-none');
        }
    }

    $spinner.classList.add('d-none');
    $results.classList.remove('d-none');

    const resetButton = noResultsMessage.querySelector('button') as HTMLButtonElement;
    const searchInput = document.getElementById('searchbarText') as HTMLInputElement;
    resetButton.addEventListener('click', () => {
        searchInput.value = '';
        searchInput.focus();
    });
}

function formatDuration(duration: string): string {
    const regex = /^(\d+)m(\d+)s$/;
    const match = regex.exec(duration);

    if (match) {
        const minutes = match[1];

        // Return the formatted string with only minutes
        return `${minutes} min read`;
    } else {
        return 'Invalid format';
    }
}

function createDiscoverResults(discoverResults: searchResp['docs'], resultsLength: number, facetDist?: Map<string, number>) {
    let discoverResultsSlice: searchResp['docs'] = [];
    const $articleContainer = document.querySelector('#discover-container') as HTMLElement;
    const $innerContainer = $articleContainer.querySelector('.articles-inner-container') as HTMLElement;

    const $viewMoreBtn = $articleContainer.querySelector('.view-more-btn') as HTMLAnchorElement;
    $viewMoreBtn.href = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}filter=${encodeURIComponent("page = 'discover'")}`;

    const $header = $articleContainer.querySelector('.articles-header') as HTMLElement;
    if (discoverResults.length === resultsLength) {
        $header.classList.add('d-none');
        discoverResultsSlice = discoverResults;
        $viewMoreBtn.classList.add('d-none');
    } else {
        const jsonCount = discoverResults.filter(result => result.url.endsWith('.json')).length;
        $header.textContent = `Articles ${facetDist?.get('discover') ? `(${(facetDist.get('discover') || 0) - jsonCount})` : ''}`;
        discoverResultsSlice = discoverResults.slice(0, 4);
    }

    for (const discoverResult of discoverResultsSlice) {
        if (discoverResult.url.endsWith('.json')) {
            continue;
        }
        const $articleTemplate = template.Clone(document.querySelector('#blog-article-template') as HTMLTemplateElement).firstElementChild as HTMLElement;

        // Set Image
        const $imgContainer = $articleTemplate.querySelector('.blog-article-image') as HTMLAnchorElement;
        $imgContainer.href = discoverResult.url;
        const $img = document.createElement('img');
        if (discoverResult.images[0]) {
            $img.src = discoverResult.images[0];
        } else {
            $img.src = (document.querySelector('#logo-placeholder img') as HTMLImageElement).src;
        }
        $imgContainer.appendChild($img);

        // Set Title
        const $title = $articleTemplate.querySelector('.blog-article-title') as HTMLElement;
        $title.textContent = discoverResult.title;

        // Set Date and Read Length
        const $discoverDate = $articleTemplate.querySelector('.discover-date') as HTMLElement;
        const $discoverDateDate = $discoverDate.querySelector('.date') as HTMLElement;
        $discoverDateDate.textContent = new Date(discoverResult.date * 1000).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });

        if (discoverResult.params.readingduration) {
            const $readLength = $discoverDate.querySelector('.read-length') as HTMLElement;
            $readLength.textContent = formatDuration(discoverResult.params.readingduration);
        }

        // Set Text
        const $text = $articleTemplate.querySelector('.blog-article-text') as HTMLElement;
        $text.innerHTML = discoverResult._formatted.summary;
        if (discoverResult.tags.length > 0) {
            const $tagContainerSm = $articleTemplate.querySelector('.tag-container-sm') as HTMLElement;
            const $tagContainerLg = $articleTemplate.querySelector('.tag-container-lg') as HTMLElement;
            for (const tag of discoverResult.tags) {
                for (const $tagContainer of [$tagContainerSm, $tagContainerLg]) {
                    const $tag = document.createElement('a');
                    $tag.classList.add('tag', 'tag-sm');
                    $tag.innerHTML = tag.title;
                    $tag.href = tag.relPermalink;
                    $tagContainer.appendChild($tag);
                }
            }
        }

        // Set URL
        const $readMoreBtns = Array.from($articleTemplate.querySelectorAll('.read-more-btn')) as HTMLAnchorElement[];
        for (const $readMoreBtn of $readMoreBtns) {
            $readMoreBtn.href = discoverResult.url;
        }

        // Set href
        const $articleContainerAnchor = $articleTemplate.querySelector('.article-container') as HTMLAnchorElement;
        $articleContainerAnchor.href = discoverResult.url;

        $innerContainer.appendChild($articleTemplate);
    }
    $articleContainer.classList.remove('d-none');
}

async function createUsesResults(results: searchResp['docs'], resultsLength: number, facetDist?: Map<string, number>) {
    const $usesContainer = document.querySelector('#uses-container') as HTMLElement;
    const $innerContainer = $usesContainer.querySelector('.uses-inner-container') as HTMLElement;

    const $viewMoreBtn = $usesContainer.querySelector('.view-more-btn') as HTMLAnchorElement;
    $viewMoreBtn.href = Relref('/uses');

    const $header = $usesContainer.querySelector('.uses-header') as HTMLElement;
    if (results.length === resultsLength) {
        $header.classList.add('d-none');
        $viewMoreBtn.parentElement?.classList.add('d-none');
    } else {
        const jsonCount = results.filter(result => result.url.endsWith('.json')).length;
        $header.textContent = `Uses ${facetDist?.get('use') ? `(${(facetDist.get('use') || 0) - jsonCount})` : ''}`;
        $viewMoreBtn.parentElement?.classList.remove('d-none');
        results = results.slice(0, 12);
    }

    for (const result of results) {
        if (result.url.endsWith('.json')) {
            continue;
        }
        const $useTemplate = template.Clone(document.querySelector('#use-card-template') as HTMLTemplateElement).firstElementChild as HTMLElement;
        const $link = $useTemplate.querySelector('a') as HTMLAnchorElement;

        // Set Image
        const $imgContainer = $useTemplate.querySelector('.use-img-container') as HTMLElement;
        const $img = document.createElement('img');
        if (result.images?.[0]) {
            $img.src = result.images[0];
        } else {
            $img.src = (document.querySelector('#logo-placeholder img') as HTMLImageElement).src;
        }
        $img.classList.add('use-img', 'object-fit-cover');
        $img.style.width = '100%';
        $imgContainer.appendChild($img);

        // Set Title
        const $title = $useTemplate.querySelector('.use-card-title') as HTMLElement;
        $title.textContent = result.title;

        // Set Text
        const sizes = await GetSizes(result.params.product, result.params.producttype);
        const minQty = await LowestQuantity(result.params.product, result.params.producttype);
        const minSize = sizes?.[0] ? sizes[0].width.toString() : '0';

        const minQtyPricePerUnit = await GetPrice(result.params.product, result.params.producttype, `${minSize}"`, minQty?.quantity || 0);
        const minQtyPrice = Number(minQtyPricePerUnit) * (minQty?.quantity || 0);
        const $text = $useTemplate.querySelector('.use-card-text') as HTMLElement;
        $text.textContent = `${minQty?.quantity || 0} ${productShortName(result.params.product)} starting at ${DollarFormat(minQtyPrice.toFixed(2))}`;

        // Set href
        $link.href = result.url;

        $innerContainer.appendChild($useTemplate);
    }
    $usesContainer.classList.remove('d-none');
}

function createFaqResults(faqResults: searchResp['docs'], resultsLength: number, facetDist?: Map<string, number>) {
    const $faqContainer = document.querySelector('#faq-container') as HTMLElement;
    const $innerContainer = $faqContainer.querySelector('.faq-inner-container') as HTMLElement;

    const $header = $faqContainer.querySelector('.faq-header') as HTMLElement;
    if (faqResults.length === resultsLength) {
        $header.classList.add('d-none');
    } else {
        $header.textContent = `FAQs ${facetDist?.get('faq') ? `(${facetDist.get('faq') || 0})` : ''}`;
    }

    for (const faqResult of faqResults) {
        const $faqTemplate = template.Clone(document.querySelector('#faq-template') as HTMLTemplateElement).firstElementChild as HTMLElement;
        ($faqTemplate.querySelector('.question') as HTMLElement).textContent = faqResult.title;

        const converter = new Converter();
        ($faqTemplate.querySelector('.answer') as HTMLElement).innerHTML = converter.makeHtml(faqResult.html);

        const $questionContainer = $faqTemplate.querySelector('.question-container') as HTMLElement;
        const $answerContainer = $faqTemplate.querySelector('.answer-container') as HTMLElement;

        const uuid = `faq-${UUID()}`;
        $questionContainer.setAttribute('data-bs-target', `#${uuid}`);
        $questionContainer.setAttribute('aria-controls', uuid);
        $answerContainer.setAttribute('id', uuid);
        $innerContainer.appendChild($faqTemplate);
    }
    const $viewMoreBtn = $faqContainer.querySelector('.view-more-faq-btn')?.parentElement as HTMLElement;
    $viewMoreBtn.classList.remove('d-none');

    $faqContainer.classList.remove('d-none');
}

function productShortName(productName: string): string {
    switch (productName) {
    case 'Lapel Pins':
        return 'pins';
    case 'Challenge Coins':
        return 'coins';
    case 'Patches':
        return 'patches';
    case 'Lanyards':
        return 'lanyards';
    case 'Keychains':
        return 'keychains';
    case 'Medals':
        return 'medals';
    case 'Belt Buckles':
        return 'buckles';
    default:
        return 'units';
    }
}